import { useCallback, useRef } from "react";

import {
  Box,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import { ConvertedPriceTooltip } from "components/ConvertedPriceTooltip";
import { EVOTRUX_BORDER_GREY } from "constants/index";
import {
  useCompanyProfile,
  useDwellObserver,
  useRoleContext,
  useShipperShipmentPageContext,
} from "hooks";
import {
  capitalizeFirst,
  formatQuotingUnitOfMeasure,
  formatText,
  getEquipmentMismatchMessage,
  getEquipmentMismatchStyling,
  getNoteLabel,
  hasEquipmentMismatch,
  ISOStringToReadableNotLocalized,
  roleOwnsLatestQuote,
} from "utils";

import { getStickyActionsCellSx } from "../constants";
import { QuoteActionsColumn } from "./QuoteActionsColumn";
import { QuoteCarrierColumn } from "./QuoteCarrierColumn";
import { QuoteNote } from "./QuoteNote";
import { QuoteScoreBand } from "./QuoteScoreBand";
import { QuoteTrackingLinkCell } from "./QuoteTrackingLinkCell";

import type { AddCarrierAnnouncement } from "../../AddCarrierToNetworkButton";
import type { QuoteViewType } from "../types";

interface Props {
  quote: ShipperCondensedQuote;
  shipment: ShipperCondensedShipment;
  onQuoteAction: (action: QuoteViewType, quote: ShipperCondensedQuote) => void;
  notificationData: { count: number; notificationIds: number[] };
  hasHorizontalOverflow: boolean;
  onQuoteViewed?: (quoteId: number) => void;
  sentFollowRequests: FollowShipperRequestAndResponse[] | null;
  carrierJustInvited: boolean;
  onAddPartnerStarted: (carrierId: number) => void;
  onAddPartnerOutcome: (
    carrierId: number,
    announcement: AddCarrierAnnouncement,
  ) => void;
  cadPerUsd: ExchangeRate | null;
  usdPerCad: ExchangeRate | null;
}

// Heavier than the cell rules inside a quote, so each quote and the band above
// it read as one block.
const QUOTE_DIVIDER = `2px solid ${EVOTRUX_BORDER_GREY}`;

export function ShipmentQuoteTableRow(props: Props) {
  const {
    quote,
    shipment,
    onQuoteAction,
    notificationData,
    hasHorizontalOverflow,
    onQuoteViewed,
    sentFollowRequests,
    carrierJustInvited,
    onAddPartnerStarted,
    onAddPartnerOutcome,
    cadPerUsd,
    usdPerCad,
  } = props;
  const { companyProfile } = useCompanyProfile(
    quote.created_by_company?.id || null,
  );
  const { role } = useRoleContext();
  const { scoreByQuoteId } = useShipperShipmentPageContext();
  const score = scoreByQuoteId.get(quote.quote);

  const rowRef = useRef<HTMLTableRowElement>(null);
  const handleDwell = useCallback(() => {
    onQuoteViewed?.(quote.quote);
  }, [onQuoteViewed, quote.quote]);
  useDwellObserver({ elementRef: rowRef, onDwell: handleDwell });

  // Check for equipment mismatch
  const equipmentMismatch = hasEquipmentMismatch(quote, shipment);
  const equipmentMismatchMessage = getEquipmentMismatchMessage(quote, shipment);

  // Format equipment type for display
  const formattedEquipmentType = quote?.equipment_type
    ? capitalizeFirst(formatText(quote.equipment_type))
    : "-";

  // Format price display - split into price and unit
  const priceAmount =
    quote?.price && quote?.currency ? `$${quote.price} ${quote.currency}` : "-";

  const priceUnit =
    quote?.quoting_unit_of_measure && quote.quoting_unit_of_measure !== "ALL_IN"
      ? formatQuotingUnitOfMeasure(quote.quoting_unit_of_measure, false)
      : "All-in";

  // A quote with a note is two table rows. The carrier and actions cells stretch
  // over both so those blocks stay whole, and the cells between them drop their
  // bottom border so the pair reads as one quote rather than two. Whichever
  // cells end the quote carry the divider to the next one.
  const showNote = Boolean(
    quote.notes &&
    !(
      quote.internal_state === "COUNTERED" &&
      role &&
      roleOwnsLatestQuote(quote, role)
    ),
  );
  const bottomCellSx = {
    verticalAlign: "top" as const,
    borderBottom: QUOTE_DIVIDER,
  };
  const middleCellSx = showNote
    ? { verticalAlign: "top" as const, borderBottom: "none" }
    : bottomCellSx;

  return (
    <>
      {score && (
        <QuoteScoreBand
          quote={quote}
          score={score}
          hasHorizontalOverflow={hasHorizontalOverflow}
        />
      )}
      <TableRow
        ref={rowRef}
        sx={{
          "&:hover": { "& .hover-actions": { opacity: 1 } },
          // The spanned cells are taller than this row's own content, and the
          // browser hands the excess to this row unless it is pinned to its
          // content height — which would push the note away from its quote.
          ...(showNote && { height: "1px" }),
        }}
      >
        {/* CARRIER */}
        <TableCell rowSpan={showNote ? 2 : 1} sx={bottomCellSx}>
          <QuoteCarrierColumn
            quote={quote}
            sentFollowRequests={sentFollowRequests}
            carrierJustInvited={carrierJustInvited}
            onAddPartnerStarted={onAddPartnerStarted}
            onAddPartnerOutcome={onAddPartnerOutcome}
          />
        </TableCell>

        {/* PRICE */}
        <TableCell sx={middleCellSx}>
          <Stack spacing={1}>
            <ConvertedPriceTooltip
              price={quote.price}
              currencyCode={quote.currency}
              cadPerUsd={cadPerUsd}
              usdPerCad={usdPerCad}
            >
              <Typography
                variant="h6"
                color="black"
                sx={{ fontWeight: "bold", lineHeight: 1.2 }}
              >
                {priceAmount}
              </Typography>
            </ConvertedPriceTooltip>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.2 }}
            >
              {priceUnit}
            </Typography>
          </Stack>
        </TableCell>

        {/* EQUIPMENT */}
        <TableCell sx={middleCellSx}>
          <Box sx={getEquipmentMismatchStyling(equipmentMismatch)}>
            {equipmentMismatch ? (
              <Tooltip title={equipmentMismatchMessage}>
                <Typography variant="body2">
                  {formattedEquipmentType}
                </Typography>
              </Tooltip>
            ) : (
              <Typography variant="body2">{formattedEquipmentType}</Typography>
            )}
          </Box>
        </TableCell>

        {/* PICKUP */}
        <TableCell sx={middleCellSx}>
          <Typography variant="body2">
            {quote?.pickup_start
              ? ISOStringToReadableNotLocalized(quote.pickup_start)
              : "-"}
          </Typography>
        </TableCell>

        {/* DELIVERY */}
        <TableCell sx={middleCellSx}>
          <Typography variant="body2">
            {quote?.delivery_eta
              ? ISOStringToReadableNotLocalized(quote.delivery_eta)
              : "-"}
          </Typography>
        </TableCell>

        {/* LINK */}
        <TableCell sx={middleCellSx} align="center">
          <QuoteTrackingLinkCell linkOffered={quote.tracking_link_offered} />
        </TableCell>

        {/* ACTIONS */}
        <TableCell
          rowSpan={showNote ? 2 : 1}
          sx={{
            backgroundColor: "background.paper",
            verticalAlign: "top",
            borderBottom: QUOTE_DIVIDER,
            ...getStickyActionsCellSx(hasHorizontalOverflow, 2),
          }}
        >
          <QuoteActionsColumn
            quote={quote}
            onQuoteAction={onQuoteAction}
            notificationData={notificationData}
            insuranceURL={companyProfile?.carrier_insurance?.url}
          />
        </TableCell>
      </TableRow>
      {showNote && quote.notes && (
        <TableRow>
          <TableCell colSpan={5} sx={{ ...bottomCellSx, pt: 0 }}>
            <QuoteNote
              label={getNoteLabel(role)}
              note={quote.notes}
              quoteId={quote.quote}
              shipmentId={quote.shipment}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
